/* eslint-disable spire/export-styles */
import useWindowSize from "@DSG/Common/Hooks/useWindowSize";
import { bodyFonts, buttonFonts, displayHeading, headlineFonts } from "@DSG/MobiusOverrides/styles/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img from "@insite/mobius/Img/Img";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import parse from "html-react-parser";
import * as React from "react";
import { FC, useEffect } from "react";
import styled, { css } from "styled-components";

const enum fields {
    bannerType = "bannerType",
    backgroundImage = "backgroundImage",
    imageAltText = "imageAltText",
    fullWidthBannerWithoutOverlay = "fullWidthBannerWithoutOverlay",
    fullWidthBannerWithOverlay = "fullWidthBannerWithOverlay",
    threeColumnBannerSidePanel = "threeColumnBannerSidePanel",
    twoEqualWidthColumnBanner = "twoEqualWidthColumnBanner",
    contentImageBanner = "contentImageBanner",
    overlayBackground = "overlayBackground",
    bannerContentPosition = "bannerContentPosition",
    sidePanelContentColumn1 = "sidePanelContentColumn1",
    sidePanelContentColumn2 = "sidePanelContentColumn2",
    iconImageOverBanner = "iconImageOverBanner",
    iconImageAltText = "iconImageAltText",
    background = "background",
    backgroundColor = "backgroundColor",
    swapBannerAndSidepanelPosition = "swapBannerAndSidepanelPosition",
    backgroundImagePosition = "backgroundImagePosition",
    enableCTARedirectOnSamePage = "enableCTARedirectOnSamePage",
    showSameImageForMobile = "showSameImageForMobile",
    mobileBackgroundImage = "mobileBackgroundImage",
    showContentBelowBannerImage = "showContentBelowBannerImage",
}

interface Props extends WidgetProps {
    fields: {
        [fields.bannerType]: string;
        [fields.bannerContentPosition]: string;
        [fields.fullWidthBannerWithoutOverlay]: boolean;
        [fields.fullWidthBannerWithOverlay]: boolean;
        [fields.threeColumnBannerSidePanel]: boolean;
        [fields.twoEqualWidthColumnBanner]: boolean;
        [fields.backgroundImage]: string;
        [fields.contentImageBanner]: string;
        [fields.overlayBackground]: string;
        [fields.iconImageOverBanner]: string;
        [fields.iconImageAltText]: string;
        [fields.background]: "image" | "color";
        [fields.backgroundColor]: string;
        [fields.sidePanelContentColumn1]: string;
        [fields.sidePanelContentColumn2]: string;
        [fields.swapBannerAndSidepanelPosition]: boolean;
        [fields.backgroundImagePosition]: "left" | "center" | "right";
        [fields.enableCTARedirectOnSamePage]: boolean;
        [fields.showSameImageForMobile]: boolean;
        [fields.mobileBackgroundImage]: string;
        [fields.imageAltText]: string;
        [fields.showContentBelowBannerImage]: boolean;
    };
}

export interface DsgHeroBannerWidgetStyles {
    mainHeroBannerWrapper?: InjectableCss;
    bannerWrapper?: InjectableCss;
    bannerWrapperInner?: InjectableCss;
    bannerImageWrapper?: InjectableCss;
    wrapper?: InjectableCss;
    iconImageWrapper?: InjectableCss;
    threeColumnBanner?: InjectableCss;
    imgWrapper?: InjectableCss;
    threeColumnImgWrapper?: InjectableCss;
}

const dsgHeroBannerWidgetStyles = {
    mainHeroBannerWrapper: {
        css: css`
           margin-bottom: 24px;
        `
    },
    bannerWrapper: {
        css: css`
            height: 100%;
            background-repeat: no-repeat;
            background-size: cover;
            border-radius: 8px;
            display: flex;
            align-items: center;
            position: relative;
            h1 {
                font: ${displayHeading.displaySemiboldSm.fontWeight} ${displayHeading.displaySemiboldSm.fontSize}/${displayHeading.displaySemiboldSm.lineHeight} ${displayHeading.displaySemiboldSm.fontFamily};
                margin-bottom: 24px;
            }
            p {
                font: ${bodyFonts.body1RegularLg.fontWeight} ${bodyFonts.body1RegularLg.fontSize}/${bodyFonts.body1RegularLg.lineHeight} ${bodyFonts.body1RegularLg.fontFamily};
                margin-bottom: 24px;
            }
            [class*="StyledButton"],
            [class*="StyledClickable"] {
                & {
                    min-height: 38px;
                    padding: 5px 24px;
                    border-radius: 30px;
                    font: ${buttonFonts.buttonSm.fontWeight} ${buttonFonts.buttonSm.fontSize} /
                        ${buttonFonts.buttonSm.lineHeight} ${buttonFonts.buttonSm.fontFamily};
                    text-align: center;
                    justify-content: center;
                }
                span {
                    display: flex;
                    align-items: center;
                    &[class*="LazyImageStyle"] {
                        padding: 0 !important;
                        display: flex !important;
                    }
                }
                &,
                &:hover,
                &:focus,
                &:active {
                    span {
                        color: inherit;
                    }
                }
            }
            .colorStyledWrapper {
                display: none;
            }
            &.simpleBannerWithOverlay {
                .bannerContentSimpleOverlay {
                    height: auto;
                    position: absolute;
                    top: auto;
                    left: 24px;
                    padding: 24px;
                    bottom: auto;
                    right: 24px;
                    width: auto;
                }
            }
            &.simpleBannerWithoutOverlay {
                .simpleBannerWithoutOverlayContent {
                    padding: 40px;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                css`
                    max-height: unset;
                    &.twoBlockBanner {
                        flex-direction: column-reverse;
                        img {
                            width: 100%;
                            height: auto;
                            border-radius: 8px 8px 0 0;
                        }
                        .bannerContentTwoBlockOverlay {
                            border-radius: 0 0 8px 8px;
                        }
                        .colorStyledWrapper {
                            display: flex;
                            border-radius: 8px 8px 0 0;
                            width: 100%;
                        }
                    }
                    &.simpleBannerWithoutOverlay {
                        &.contentBelowBanner {
                            > div {
                                gap: 16px;
                                align-items: center;
                            }
                            .simpleBannerWithoutOverlayContent {
                                padding: 0;
                            }
                        }
                    }
                `,
            ], "max")}

            @media only screen and (max-width: 1024px) {
                display: flex;
                align-items: center;
                justify-content: flex-start;
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    background-position: center;
                `,
            ], "minMax")}
            &.twoBlockBanner {
                [class*="BannerContentWrapper"] {
                    margin: 0 !important;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    h1 {
                        font-size: clamp(16px, calc(16px + (38 - 16) * (100vw - 768px)/(1440 - 768)), 38px) !important;
                        line-height:clamp(24px,calc(24px + (49 - 24) * (100vw - 768px)/(1440 - 768)),49px) !important;
                        margin-bottom: 8px !important;
                    }
                    p {
                        font-size: clamp(12px, calc(12px + (16 - 12) * (100vw - 768px)/(1440 - 768)), 16px) !important;
                        line-height: clamp(18px, calc(18px + (24 - 18) * (100vw - 768px)/(1440 - 768)), 24px) !important;
                        margin-bottom: 0 !important;
                    }
                    &.simpleBannerWithOverlay {
                        .bannerContentSimpleOverlay {
                            left: 40px;
                            right: auto;
                            &.alignRight {
                                left: auto;
                                right: 40px;
                            }
                        }
                    }
                    &.simpleBannerWithoutOverlay {
                        .simpleBannerWithoutOverlayContent {
                            padding: 0 40px;
                        }
                    }
                    &.simpleBannerWithoutOverlay {
                        .simpleBannerWithoutOverlayContent,
                        > div {
                            width: 100%;
                        }
                    }
                    [class*="StyledButton"],
                    [class*="StyledClickable"] {
                        margin-top: clamp(12px, calc(12px + (24 - 12) * (100vw - 768px)/(1440 - 768)), 24px) !important;
                    }
                    &.twoBlockBanner {
                        [class*="BannerContentWrapper"] {
                            max-width: 325px !important;
                            border-radius: 8px;
                            width: 100%;
                            &:before {
                                max-width: 325px;
                                z-index: 0;
                                border-radius: 8px 0 0 8px;
                            }
                            > div {
                                z-index: 1;
                            }
                        }
                        > img {
                            max-width: calc(100% - 325px);
                            position: relative;
                            height: 100%;
                            width: 100%;
                            right: 0;
                            border-radius: 0 8px 8px 0;
                        }
                        &.rightAlignBanner {
                            justify-content: flex-end;
                            flex-direction: row-reverse;
                            [class*="BannerContentWrapper"] {
                                &:before {
                                    right: 0;
                                    left: auto;
                                    border-radius: 0 8px 8px 0;
                                }
                            }
                            > img {
                                left: 0;
                                right: auto;
                                border-radius: 8px 0 0 8px;
                            }
                        }
                    }
                    &.simpleBannerWithoutOverlay {
                        .simpleBannerWithoutOverlayContent {
                            height: auto;
                            position: absolute;
                            top: auto;
                            bottom: auto;
                        }
                    }
                `,
                css`
                    [class*="StyledButton"],
                    [class*="StyledClickable"] {
                        padding: 6px 24px;
                        min-height: 48px;
                        border-radius: 30px;
                        font-size: ${buttonFonts.buttonLg.fontSize};
                        line-height: ${buttonFonts.buttonLg.lineHeight};
                    }
                    &.twoBlockBanner {
                        [class*="BannerContentWrapper"] {
                            max-width: 486px !important;
                            &:before {
                                max-width: 486px;
                            }
                        }
                        > img {
                            max-width: calc(100% - 486px);
                        }
                    }
                    &.simpleBannerWithoutOverlay {
                        .simpleBannerWithoutOverlayContent {
                            padding: 40px 80px;
                        }
                        &.contentBelowBanner {
                            .simpleBannerWithoutOverlayContent {
                                padding: 0;
                            }
                        }
                    }
                `
            ], "min")}
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    &.twoBlockBanner {
                        .bannerContentTwoBlockOverlay {
                            padding: 12px 16px;
                        }
                    }
                `,
            ], "minMax")}
            @media screen and (min-width: 1280px) {
                &.simpleBannerWithOverlay {
                    .bannerContentSimpleOverlay {
                        left: 142px;
                        &.alignRight {
                            right: 142px;
                        }
                    }
                }
            }
           
                
        `,
    },
    bannerWrapperInner: {
        css: css`
            display: flex;
            align-items: center;
            height: 100%;
            column-gap: 48px;

            @media only screen and (max-width: 767px) {
                flex-direction: column;
                align-items: flex-start;
                row-gap: 40px;
                width: 100%;
                column-gap: 0;
            }

            &.bannerContentTwoBlockOverlay {
                padding: 40px 48px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    &.bannerContentTwoBlockOverlay {
                        max-width: 325px;
                        padding: 20px 24px;
                    }
                    &.bannerContentSimpleOverlay {
                        > div {
                            max-width: 325px;
                            height: auto;
                        }
                    }
                `,
                css`
                    &.bannerContentTwoBlockOverlay {
                        max-width: 486px;
                    }
                    &.bannerContentSimpleOverlay {
                        > div {
                            max-width: 410px;
                        }
                    }
                `
            ], "min")}
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                css`
                    &.withBackgroundOnContent {
                        justify-content: center;
                    }
                    &.withoutBackgroundOnContent {
                        padding-right: 80px
                    }
                `,
            ], "max")}
            &.bannerContentSimpleOverlay {
                padding: 24px;
                border-radius: 8px;
                margin: 0 !important;
            }
        `
    },
    iconImageWrapper: {
        css: css`
            width: 88px;
            display: flex;
            img {
                max-width: 88px;
                height: auto;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        width: 200px;
                        img {
                            max-width: 200px;
                        }

                    `,
                    null,
                ], "min")}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    css`
                        padding-bottom: 24px;
                    `,
                ], "max")}
        `
    },
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: center;
            height: 100%;
            &.simpleBannerWithoutOverlayContent {
                flex-direction: column-reverse;
                &,
                > .contentWrapper {
                    margin: 0 !important;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    &.simpleBannerWithoutOverlayContent {
                        flex-direction: row;
                        flex-wrap: nowrap;
                        justify-content: space-between;
                        column-gap: 40px;
                        align-items: center;
                    }
                `,
                null,
            ], "min")}
        `,
    },
    threeColumnBanner: {
        css: css`
            .bannerContentSection {
                > div {
                    width: 100%;
                }
            }
            @media screen and (min-width: 1200px){
                display: flex;
                column-gap: 16px;
                &.rightAlignBanner {
                    > div:first-child {
                        order: 1;
                    }
                }
                .bannerContentSection {
                    max-width: 66.66%;
                    width: 100%;
                }
                .wrapper {
                    left: 52px;
                    right: 52px;
                    padding-top: 40px;
                }
                .sidePanelSection {
                    max-width: 33.33%;
                    width: 100%;
                }
            }
            .bannerContentSection  {
                align-items: flex-start;
            }
            @media screen and (min-width: 768px) and (max-width: 1279px) {
                .wrapper {
                    left: 24px;
                    right: 24px;
                    padding-top: 24px;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                css`
                    .bannerContentSection {
                        padding: 62px 40px;
                        .bannerContentWrapper > div {
                            max-width: 100% !important;
                            p {
                                max-width: 90%;
                            }
                        }
                    }   
                `,
            ], "max")}
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    .bannerContentSection {
                        &.left {
                            background-position: left;
                        }
                        &.center {
                            background-position: center;
                        }
                        &.right {
                            background-position: right;
                        }
                    }
                    .wrapper {
                        position: absolute;
                        top: 0;
                        justify-content: flex-start;
                    }
                `,
            ], "min")}
        `
    },
    sidePanel: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 16px;
            padding: 16px 16px 0;
            > div {
                padding: 24px;
                border-radius: 8px;
                gap: 16px;
                display: flex;
                flex-direction: column;
                flex: 1;
            }
            p {
                font: ${bodyFonts.body1SemiBoldLg.fontWeight} ${bodyFonts.body1SemiBoldLg.fontSize}/${bodyFonts.body1SemiBoldLg.lineHeight} ${bodyFonts.body1SemiBoldLg.fontFamily};
                margin-bottom: 0;
                width: 100%;
            }
            [class*="StyledButton"],
            [class*="StyledClickable"] {
                & {
                    min-height: 38px;
                    padding: 3px 24px;
                    border-radius: 30px;
                    font: ${buttonFonts.buttonSm.fontWeight} ${buttonFonts.buttonSm.fontSize} /
                        ${buttonFonts.buttonSm.lineHeight} ${buttonFonts.buttonSm.fontFamily};
                    text-align: center;
                    justify-content: center;
                    align-self: flex-start;
                }
                span {
                    display: flex;
                    align-items: center;
                    &[class*="LazyImageStyle"] {
                        padding: 0 !important;
                        display: flex !important;
                    }
                }
                &,
                &:hover,
                &:focus,
                &:active {
                    span {
                        color: inherit;
                    }
                }
            }
            @media screen and (min-width: 768px) and (max-width: 1179px) {
                flex-direction: row;
                > div {
                    width: 50%;
                }
            }
            @media screen and (min-width: 1200px) {
                gap: 8px;
                padding: 0;
                > div {
                    padding: clamp(12px, calc(12px + (24 - 12) * (100vw - 1200px) / (1440 - 1200)), 24px) clamp(20px, calc(20px + (40 - 20) * (100vw - 1200px) / (1440 - 1200)), 40px);
                }

                p {
                    font-size: ${headlineFonts.headline4SemiBoldLg.fontSize};
                    line-height: ${headlineFonts.headline4SemiBoldLg.lineHeight};
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    p {
                        font-size: clamp(14px, calc(14px + (20 - 14) * (100vw - 1200px)/(1440 - 1200)), 20px) !important;
                        line-height: clamp(21px, calc(21px + (30 - 21) * (100vw - 1200px)/(1440 - 1200)), 30px) !important;
                        margin-bottom: 0 !important;
                    }
                `,
            ], "min")}
        `
    },
    threeColumnImgWrapper: {
        css: css`
            border-radius: 8px;
            width: 100%;
            display: flex;
            img {
                width: 100%;
                height: auto;
                border-radius: 8px;
            }
        `
    },
    imgWrapper: {
        css: css`
            border-radius: 8px;
            width: 100%;
            display: flex;
            img {
                width: 100%;
                border-radius: 8px;
                height: auto;
            }
        `
    }
};

export const DsgHeroBannerWidget: FC<Props> = ({ fields })  => {
    const styles = useMergeStyles("richContent", dsgHeroBannerWidgetStyles);
    const { width } = useWindowSize();
    useEffect(() => {
        const buttons = document.querySelectorAll('.scrollOntoSpecifiedSection');
        const handleClick = (event: Event) => {    
            const target = event.currentTarget as HTMLButtonElement;
            const sectionRedirect = target.getAttribute('data-redirectSection');
            if (sectionRedirect) {
                const sectionElement = document.querySelector(`.${sectionRedirect}`);
                if (sectionElement) {
                    sectionElement.scrollIntoView({ behavior: 'smooth' });
                }
            }
        };

        if (buttons) {
            buttons.forEach(button => button.addEventListener('click', handleClick));
        } 

        return () => {
            if (buttons) {
                buttons.forEach(button => button.removeEventListener('click', handleClick));
            }
        };
        
    }, [fields.enableCTARedirectOnSamePage]);

    return (
        <StyledWrapper {...styles.mainHeroBannerWrapper}>
            {fields.bannerType === "simple-banner-without-overlay" && (
                <StyledWrapper
                    style={{   
                        ...(fields.background === "image" 
                            ? (width <= 767 && !fields.mobileBackgroundImage
                                ? { backgroundImage: `url(${fields.backgroundImage})` } 
                                : {}
                            )
                            : { backgroundColor: fields.backgroundColor }
                        ),
                    }} 
                    {...styles.bannerWrapper} className={`simpleBannerWithoutOverlay withCTARedirectSamePage ${fields.showContentBelowBannerImage ? 'contentBelowBanner' : ''}`}>
                    <StyledWrapper {...styles.bannerWrapperInner}>
                        {fields.backgroundImage && width > 767 && (
                            <StyledWrapper {...styles.imgWrapper} className="simpleImgWrapper">
                                <Img src={fields.backgroundImage} altText={fields.imageAltText || ''} />
                            </StyledWrapper>
                        )}
                        {fields.backgroundImage && width < 768 && fields.mobileBackgroundImage && (
                            <StyledWrapper {...styles.imgWrapper} className="simpleImgWrapper">
                                <Img src={fields.mobileBackgroundImage} altText={fields.imageAltText || ''} />   
                            </StyledWrapper>
                        )}
                        <ContentWrapper className="simpleBannerWithoutOverlayContent" {...styles.wrapper} 
                            backgroundColor='transparent' padding="" bannerContentPosition="">
                            <StyledWrapper className="contentWrapper">
                                {parse(fields.contentImageBanner, parserOptions)}
                            </StyledWrapper>
                            {fields.iconImageOverBanner && (
                                <StyledWrapper {...styles.iconImageWrapper}>
                                    <Img src={fields.iconImageOverBanner} altText={fields.iconImageAltText || ''}/>
                                </StyledWrapper>
                            )}
                        </ContentWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            )}
            {fields.bannerType === "simple-banner-with-overlay" && (
                <StyledWrapper 
                    style={{ 
                        justifyContent: fields.bannerContentPosition || 'flex-start',
                        ...(fields.background === "image" ? '' : { backgroundColor: fields.backgroundColor }),
                    }} 
                    {...styles.bannerWrapper} className="simpleBannerWithOverlay">
                    {fields.backgroundImage && (
                        <StyledWrapper {...styles.imgWrapper} className="simpleImgWrapper">
                            <Img src={fields.backgroundImage} altText={fields.imageAltText || ''} />
                        </StyledWrapper>
                    )}
                 
                    <BannerContentWrapper {...styles.bannerWrapperInner}
                        bannerContentPosition={fields.bannerContentPosition ? fields.bannerContentPosition : 'flex-start'}
                        className={`bannerContentSimpleOverlay ${fields.bannerContentPosition === "flex-start" ? '' : 'alignRight'}`}
                        backgroundColor={fields.overlayBackground ? fields.overlayBackground : 'transparent'} padding="">
                        <StyledWrapper>
                            {parse(fields.contentImageBanner, parserOptions)}
                        </StyledWrapper>
                    </BannerContentWrapper>
                </StyledWrapper>
                
            )}
            {fields.bannerType === "three-block-banner" && (
                <StyledWrapper {...styles.threeColumnBanner} className={` ${fields.swapBannerAndSidepanelPosition  ? 'rightAlignBanner' : 'leftAlignBanner'}`}>
                    <StyledWrapper 
                        style={{ 
                            ...(fields.background === "image" 
                                ? (width <= 767 
                                    ? { backgroundImage: `url(${fields.backgroundImage})` } 
                                    : {}
                                )
                                : { backgroundColor: fields.backgroundColor }
                            ),
                        }} 
                        {...styles.bannerWrapper} className={`bannerContentSection ${fields.backgroundImagePosition}`}>
                        <StyledWrapper {...styles.bannerWrapperInner} >
                            {(fields.backgroundImage && width > 767 ) && (
                                <StyledWrapper {...styles.threeColumnImgWrapper}>
                                    <Img src={fields.backgroundImage} altText={fields.imageAltText || ''}/>
                                </StyledWrapper>
                            )}
                            <ContentWrapper className="wrapper header-content" {...styles.wrapper} 
                                backgroundColor='transparent' padding="">
                                <StyledWrapper className="bannerContentWrapper">
                                    {parse(fields.contentImageBanner, parserOptions)}
                                </StyledWrapper>
                            </ContentWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.sidePanel} className="sidePanelSection">
                        {fields.sidePanelContentColumn1 && (
                            <>
                                {parse(fields.sidePanelContentColumn1, parserOptions)}
                            </>
                        )}
                        {fields.sidePanelContentColumn2 && (
                            <>
                                {parse(fields.sidePanelContentColumn2, parserOptions)}
                            </>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            )}
            {fields.bannerType === "two-block-banner" && (
                <StyledWrapper 
                    style={{ 
                        ...(fields.background === "image" ? { backgroundColor: fields.overlayBackground } : { backgroundColor: fields.backgroundColor }),
                    }}
                    {...styles.bannerWrapper} className={`twoBlockBanner ${fields.bannerContentPosition === 'flex-end' ? 'rightAlignBanner' : 'leftAlignBanner'}`}>
                    <BannerContentWrapper {...styles.bannerWrapperInner}
                        bannerContentPosition={fields.bannerContentPosition ? fields.bannerContentPosition : 'flex-start'}
                        className="bannerContentTwoBlockOverlay" 
                        backgroundColor={fields.overlayBackground ? fields.overlayBackground : 'transparent'} padding="">
                        <StyledWrapper>
                            {parse(fields.contentImageBanner, parserOptions)}
                        </StyledWrapper>
                    </BannerContentWrapper>
                    {fields.background === "image" && (
                        <Img src={fields.backgroundImage} altText={fields.imageAltText || ''} />
                    )}
                    {fields.background === "color" && (
                        <StyledWrapper className="colorStyledWrapper"  style={{
                            backgroundColor: fields.backgroundColor,
                        }}/>
                    )}
                </StyledWrapper>
            )}
        </StyledWrapper>
       
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: DsgHeroBannerWidget,
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.bannerType,
                displayName:"Content Type",
                editorTemplate: "RadioButtonsField",
                fieldType: "Contextual",
                defaultValue: "simple-banner-with-overlay",
                sortOrder: 0,
                options: [
                    {
                        displayName: "Simple hero banner without overlay",
                        value: "simple-banner-without-overlay",
                    },
                    {
                        displayName: "Simple hero banner with overlay",
                        value: "simple-banner-with-overlay",
                    },
                    {
                        displayName: "Three block hero banner",
                        value: "three-block-banner",
                    },
                    {
                        displayName: "Two block hero banner",
                        value: "two-block-banner",
                    },
                ],
            },
            {
                name: fields.background,
                displayName: "Background",
                fieldType: "Contextual",
                editorTemplate: "RadioButtonsField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                ],
                defaultValue: "image",
            },
            {
                fieldType: "Contextual",
                name: fields.backgroundImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "General",
                name: fields.imageAltText,
                editorTemplate: "TextField",
                defaultValue: "Hero Banner",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                name: fields.showSameImageForMobile,
                displayName: "Show Same Mobile Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isVisible: widget => widget.fields.background === "image" && widget.fields.bannerType === 'simple-banner-without-overlay',
            },
            {
                fieldType: "Contextual",
                name: fields.mobileBackgroundImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => (widget.fields.background === "image" && !widget.fields.showSameImageForMobile && widget.fields.bannerType === 'simple-banner-without-overlay'),
            },
            {
                name: fields.enableCTARedirectOnSamePage,
                displayName: "Enable CTA Redirect on Same Page",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: widget => widget.fields.background === "image" && widget.fields.bannerType === 'simple-banner-without-overlay',
            },
            {
                name: fields.showContentBelowBannerImage,
                displayName: "Show Content Below Banner Image Mobile",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: widget => widget.fields.background === "image" && widget.fields.bannerType === 'simple-banner-without-overlay',
            },
            {
                name: fields.backgroundImagePosition,
                displayName: "Background Image Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    {
                        displayName: "Left",
                        value: "left",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "right",
                    },
                ],
                isVisible: widget => (widget.fields.bannerType === 'three-block-banner' && widget.fields.background === "image"),
            },
            {
                name: "backgroundColor",
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                fieldType: "Contextual",
                defaultValue: "#f6f6f7",
                isVisible: widget => widget.fields.background === "color",
            },
            {
                name: fields.overlayBackground,
                displayName: "Background For Banner Content",
                editorTemplate: "ColorPickerField",
                defaultValue: "#EAEBEB",
                tab: basicTab,
                fieldType: "Contextual",
                isVisible: widget => (widget.fields.bannerType === 'simple-banner-with-overlay' || widget.fields.bannerType === 'two-block-banner'),
            },
            {
                name: fields.contentImageBanner,
                displayName: "Banner content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
                extendedConfig: { height: 200 },
                tab: basicTab,
                expandedToolbarButtons: {
                    moreMisc: {
                        buttons: [
                            "insertLink",
                            "insertImage",
                            "insertHR",
                            "specialCharacters",
                            "insertVideo",
                            "insertFile",
                        ],
                        buttonsVisible: 3,
                    },
                    code: {
                        buttons: ["selectAll", "html", "help"],
                        buttonsVisible: 3,
                    },
                },
            },
            {
                name: fields.bannerContentPosition,
                displayName: "Banner Content Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "flex-start",
                fieldType: "General",
                options: [
                    {
                        displayName: "Left",
                        value: "flex-start",
                    },
                    {
                        displayName: "Right",
                        value: "flex-end",
                    },
                ],
                isVisible: widget => (widget.fields.bannerType === 'simple-banner-with-overlay' || widget.fields.bannerType === 'two-block-banner'),
            },
            {
                name: fields.sidePanelContentColumn1,
                displayName: "Side Panel Column 1 Content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
                extendedConfig: { height: 200 },
                tab: basicTab,
                expandedToolbarButtons: {
                    moreMisc: {
                        buttons: [
                            "insertLink",
                            "insertImage",
                            "insertHR",
                            "specialCharacters",
                            "insertVideo",
                            "insertFile",
                        ],
                        buttonsVisible: 3,
                    },
                    code: {
                        buttons: ["selectAll", "html", "help"],
                        buttonsVisible: 3,
                    },
                },
                isVisible: widget => widget.fields.bannerType === 'three-block-banner',
            },
            {
                name: fields.sidePanelContentColumn2,
                displayName: "Side Panel Column 2 Content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
                extendedConfig: { height: 200 },
                tab: basicTab,
                expandedToolbarButtons: {
                    moreMisc: {
                        buttons: [
                            "insertLink",
                            "insertImage",
                            "insertHR",
                            "specialCharacters",
                            "insertVideo",
                            "insertFile",
                        ],
                        buttonsVisible: 3,
                    },
                    code: {
                        buttons: ["selectAll", "html", "help"],
                        buttonsVisible: 3,
                    },
                },
                isVisible: widget => widget.fields.bannerType === 'three-block-banner',
            },
            {
                name: fields.swapBannerAndSidepanelPosition,
                displayName: "Swap Column Position",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: widget => widget.fields.bannerType === 'three-block-banner',
            },
            {
                fieldType: "Contextual",
                name: fields.iconImageOverBanner,
                displayName: "Icon image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                  isVisible: widget => widget.fields.bannerType === 'simple-banner-without-overlay',
            },
            {
                fieldType: "General",
                name: fields.iconImageAltText,
                displayName: "Icon Image Alt Text",
                editorTemplate: "TextField",
                defaultValue: "",
                isVisible: widget => widget.fields.bannerType === 'simple-banner-without-overlay',
            },
        ],
    },
};

export default widgetModule;

export const ContentWrapper = styled.div<{ backgroundColor: string; padding: number } & InjectableCss>`
    ${({ backgroundColor }) => (backgroundColor ? `background-color: ${backgroundColor};` : null)};
    ${({ padding }) => (padding ? `padding: ${padding}px;` : null)};
    & > *:last-child {
        margin: 0;
    }
    ${injectCss}
`;
export const BannerContentWrapper = styled.div<{ bannerContentPosition: string; backgroundColor: string; padding: number } & InjectableCss>`
    ${({ backgroundColor }) => (backgroundColor ? `background-color: ${backgroundColor};` : null)};
    ${({ padding }) => (padding ? `padding: ${padding}px;` : null)};    
    justify-content: ${({ bannerContentPosition }) => bannerContentPosition || 'flex-start'};
    &.bannerContentTwoBlockOverlay {
        ${({ bannerContentPosition }) =>
        bannerContentPosition === 'flex-start'
          ? 'left: 0;'
          : 'right: 0;'}
       
    }
    ${injectCss}
`;
